import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { IntlProvider } from "react-intl";
import { BrowserRouter } from "react-router-dom";
import { App } from "./app/App";
import { AppContextProvider } from "./app/AppContext";
import { RootErrorBoundary } from "./app/ErrorBoundary";
import { en } from "./core/messages";
import { getRuntimeConfig, resolveSiteExperience } from "./core/site-experience";
import { installGlobalErrorReporting } from "./core/telemetry";
import { ErrorState } from "./shared/Shared";
import "./styles/fonts.css";
import "./styles/global.css";
import "./styles/v1-tokens.css";
import "./styles/brand.css";

installGlobalErrorReporting();

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("ROOT_ELEMENT_MISSING");

const root = createRoot(rootElement);
const runtimeConfig = getRuntimeConfig();
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

try {
  const experience = resolveSiteExperience(
    window.location.hostname,
    runtimeConfig,
    import.meta.env.VITE_SITE_ID,
  );
  // Stamp site/theme on <html> BEFORE first paint: every stylesheet keys off
  // [data-site], and leaf helpers (siteSuffix) read it. AppContext re-stamps
  // the same values in an effect (harmless, kept for HMR).
  document.documentElement.dataset.site = experience.id;
  document.documentElement.dataset.theme = experience.themeId;
  root.render(
    <StrictMode>
      <RootErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AppContextProvider experience={experience} runtimeConfig={runtimeConfig}>
              <App />
            </AppContextProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </RootErrorBoundary>
    </StrictMode>,
  );
} catch {
  document.documentElement.dataset.site = "myfirstagent";
  root.render(
    <IntlProvider locale="en" messages={en}>
      <ErrorState titleId="site.invalidTitle" bodyId="site.invalidBody" />
    </IntlProvider>,
  );
}
